import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import {
  Loading,
} from '@folio/stripes/components';

import { SourcesCollapse, CopyButton } from '../';

import css from './MessageList.css';

const propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
};

const defaultProps = {
  messages: [],
  isLoading: false,
};

const MessageList = ({
  messages,
  isLoading,
}) => {
  const intl = useIntl();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTime = (timestamp) => {
    return new Intl.DateTimeFormat('ar', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(timestamp);
  };

  const renderMessage = (message, index) => {
    const isUser = message.sender === 'user';
    const messageClass = isUser ? css.userMessage : css.botMessage;
    const containerClass = isUser ? css.userMessageContainer : css.botMessageContainer;

    return (
      <div key={message.id} className={containerClass}>
        <div className={`${css.message} ${messageClass} ${message.isError ? css.errorMessage : ''}`}>
          <div className={css.messageContent}>
            <div className={css.messageHeader}>
              <div className={css.messageTime}>
                {formatTime(message.timestamp)}
              </div>
              {!isUser && index > 0 && (
                <CopyButton
                  userMessage={messages[index - 1]?.text || ''}
                  botMessage={message.text}
                  sources={message.sources}
                />
              )}
              {isUser && (
                <CopyButton
                  userMessage={message.text}
                  botMessage=""
                  sources={[]}
                />
              )}
            </div>
            <div className={css.messageText}>
              {message.text}
            </div>
          </div>
          {!isUser && message.sources && message.sources.length > 0 && (
            <SourcesCollapse
              sources={message.sources}
              sourceCount={message.sourceCount || message.sources.length}
            />
          )}
        </div>
      </div>
    );
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={css.emptyState}>
        <div className={css.emptyStateText}>
          {intl.formatMessage({ id: 'ui-plugin-smart-help.chat.welcomeMessage' })}
        </div>
      </div>
    );
  }

  return (
    <div className={css.messagesList}>
      {messages.map((message, index) => renderMessage(message, index))}
      {isLoading && (
        <div className={css.loadingContainer}>
          <Loading />
          <div className={css.loadingText}>
            {intl.formatMessage({ id: 'ui-plugin-smart-help.chat.processingMessage' })}
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

MessageList.propTypes = propTypes;
MessageList.defaultProps = defaultProps;

export default MessageList;